import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { Plus, Search, Users, Star } from 'lucide-react';
import { useCustomers } from '../hooks/useCustomers';

/**
 * Customers component displays a list of customers with advanced filtering, searching, and status categorization.
 * 
 * - Fetches customer data from Supabase on mount.
 * - Allows searching customers by name, email, phone, or address.
 * - Provides filters for customer status: All, Active, High Value, and At Risk.
 * - Displays a table of customers with details such as contact info, value, last service, and rating.
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
 * - Allows navigation to customer detail and creation pages.
 * 
 * @component
 * @returns {JSX.Element} The rendered Customers component.
 */
export default function Customers() {
  const permissions = useRoleAccess();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const { customers: allCustomers } = useCustomers();

  // Customer status filters based on workflow insights
  const CUSTOMER_FILTERS = [
    { 
      value: 'all', 
      label: 'All Customers', 
      count: allCustomers.length 
    },
    { 
      value: 'active', 
      label: 'Active', 
      count: allCustomers.filter(c => c.rating >= 4 || c.lastService !== 'Never').length 
    },
    { 
      value: 'high-value', 
      label: 'High Value', 
      count: allCustomers.filter(c => c.totalSpent > 500).length 
    },
    { 
      value: 'at-risk', 
      label: 'At Risk', 
      count: allCustomers.filter(c => c.rating < 3 || c.lastService === 'Never').length 
    },
  ];

  // Enhanced filtering
  const filteredCustomers = allCustomers.filter(customer => {
    const matchesSearch = 
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.address?.toLowerCase().includes(searchTerm.toLowerCase());

    // Apply customer filter logic
    let matchesFilter = true;
    if (customerFilter === 'active') {
      matchesFilter = customer.rating >= 4 || customer.lastService !== 'Never';
    } else if (customerFilter === 'high-value') {
      matchesFilter = customer.totalSpent > 500;
    } else if (customerFilter === 'at-risk') {
      matchesFilter = customer.rating < 3 || customer.lastService === 'Never';
    }

    return matchesSearch && matchesFilter;
  });

  return (
    console.log('Rendering Customers with', { allCustomers, filteredCustomers, searchTerm, customerFilter }),
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1">Manage customer relationships and track their journey</p>
        </div>
        {permissions.canEditCustomers && (
          <button
            onClick={() => navigate('/customers/new')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Customer</span>
          </button>
        )}
      </div>
      {/* No results */}
      {allCustomers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No customers found matching your search.</p>
        </div>
      ) : (
        <>
      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search customers by name, email, phone, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Customer Type Filters */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Filter by Customer Type:</p>
            <div className="flex flex-wrap gap-2">
              {CUSTOMER_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setCustomerFilter(filter.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    customerFilter === filter.value
                      ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Customers ({filteredCustomers.length} total)
            </h2>
          </div>
        </div>

        {filteredCustomers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <Users className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-500">
              {searchTerm 
                ? 'Try adjusting your search terms.'
                : 'Get started by adding your first customer.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/customers/${customer.id}`)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                          <div className="text-sm text-gray-500">{customer.address}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.email}</div>
                      <div className="text-sm text-gray-500">{customer.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {typeof customer.totalSpent === 'string' ? customer.totalSpent : `$${customer.totalSpent?.toFixed(2) || '0.00'}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.lastService}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < customer.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="ml-2 text-sm text-gray-600">({customer.rating})</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}